import React, { useMemo, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Button,
  Input,
  Select,
  FormControl,
  FormErrorMessage,
  FormLabel,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { loadApplyFormData, saveApplyFormData } from "./applyFormStorage";
import { saveCustomerProfileStep } from "./applyProfileApi";
import { loadUserSession } from "./userSessionStorage";

export default function ApplyPersonalInfoPage() {
  const navigate = useNavigate();

  const initialCustomer = useMemo(() => {
    const stored = loadApplyFormData();
    const storedCustomer = stored.customer && typeof stored.customer === "object" ? stored.customer : {};

    const session = loadUserSession();
    const sessionCustomer = session.customer && typeof session.customer === "object" ? session.customer : {};
    const sessionPersonalInfo =
      sessionCustomer.personalInfo && typeof sessionCustomer.personalInfo === "object" ? sessionCustomer.personalInfo : {};

    return {
      ...sessionPersonalInfo,
      ...storedCustomer,
      province: storedCustomer.province || sessionPersonalInfo.province || "",
    };
  }, []);

  const [values, setValues] = useState({
    firstName: initialCustomer.firstName || "",
    lastName: initialCustomer.lastName || "",
    dateOfBirth: initialCustomer.dateOfBirth || "",
    phoneNumber: initialCustomer.phoneNumber || "",
    streetAddress: initialCustomer.streetAddress || "",
    city: initialCustomer.city || "",
    province: initialCustomer.province || "",
    postalCode: initialCustomer.postalCode || "",
    housingType: initialCustomer.housingType || "",
    monthlyRent: initialCustomer.monthlyRent || "",
    monthlyMortgage: initialCustomer.monthlyMortgage || "",
  });

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    dateOfBirth: false,
    phoneNumber: false,
    streetAddress: false,
    city: false,
    province: false,
    postalCode: false,
    housingType: false,
    monthlyRent: false,
    monthlyMortgage: false,
  });

  const [focusedField, setFocusedField] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const isRentSelected = values.housingType === "Rent";
  const isMortgageSelected = values.housingType === "Mortgage";

  const buildCustomerPayload = () => {
    const payload = {};

    const assignText = (key, value) => {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (trimmed) {
        payload[key] = trimmed;
      }
    };

    assignText("firstName", values.firstName);
    assignText("lastName", values.lastName);
    assignText("dateOfBirth", values.dateOfBirth);
    assignText("phoneNumber", values.phoneNumber);
    assignText("streetAddress", values.streetAddress);
    assignText("city", values.city);
    assignText("province", values.province);
    assignText("postalCode", values.postalCode);
    assignText("housingType", values.housingType);

    if (isRentSelected) {
      assignText("monthlyRent", values.monthlyRent);
    }

    if (isMortgageSelected) {
      assignText("monthlyMortgage", values.monthlyMortgage);
    }

    return payload;
  };

  const errors = {
    firstName: touched.firstName && !values.firstName ? "First name is required" : "",
    lastName: touched.lastName && !values.lastName ? "Last name is required" : "",
    dateOfBirth: touched.dateOfBirth && !values.dateOfBirth ? "Date of birth is required" : "",
    phoneNumber: touched.phoneNumber && !values.phoneNumber ? "Phone number is required" : "",
    streetAddress: touched.streetAddress && !values.streetAddress ? "Street address is required" : "",
    city: touched.city && !values.city ? "City is required" : "",
    province: touched.province && !values.province ? "Province is required" : "",
    postalCode: touched.postalCode && !values.postalCode ? "Postal code is required" : "",
    housingType: touched.housingType && !values.housingType ? "Please select rent or mortgage" : "",
    monthlyRent: isRentSelected && touched.monthlyRent && !values.monthlyRent ? "Monthly rent is required" : "",
    monthlyMortgage: isMortgageSelected && touched.monthlyMortgage && !values.monthlyMortgage ? "Monthly mortgage is required" : "",
  };

  const hasErrors = Object.values(errors).some(Boolean);

  const handleChange = (field) => (event) => {
    const nextValue = event.target.value;

    if (field === "housingType") {
      setValues((prev) => ({
        ...prev,
        housingType: nextValue,
        monthlyRent: nextValue === "Rent" ? prev.monthlyRent : "",
        monthlyMortgage: nextValue === "Mortgage" ? prev.monthlyMortgage : "",
      }));
      return;
    }

    setValues((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleFocus = (field) => () => {
    setFocusedField(field);
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFocusedField((prev) => (prev === field ? "" : prev));
  };

  const isFloating = (field) => focusedField === field || values[field] !== "";

  const setAllTouched = () => {
    setTouched({
      firstName: true,
      lastName: true,
      dateOfBirth: true,
      phoneNumber: true,
      streetAddress: true,
      city: true,
      province: true,
      postalCode: true,
      housingType: true,
      monthlyRent: true,
      monthlyMortgage: true,
    });
  };

  const persistCustomer = () => {
    saveApplyFormData({ customer: buildCustomerPayload() });
  };

  const handleBack = () => {
    persistCustomer();
    const stored = loadApplyFormData();
    const flow = stored.flow && typeof stored.flow === "object" ? stored.flow : {};
    const personalInfoBackPath = flow.personalInfoBackPath === "/apply/reapply" ? "/apply/reapply" : "/apply";
    navigate(personalInfoBackPath);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    setAllTouched();
    if (
      !values.firstName ||
      !values.lastName ||
      !values.dateOfBirth ||
      !values.phoneNumber ||
      !values.streetAddress ||
      !values.city ||
      !values.province ||
      !values.postalCode ||
      !values.housingType ||
      (isRentSelected && !values.monthlyRent) ||
      (isMortgageSelected && !values.monthlyMortgage) ||
      hasErrors
    ) {
      return;
    }

    try {
      setIsSubmitting(true);
      const customerPayload = buildCustomerPayload();
      saveApplyFormData({ customer: customerPayload });
      const stored = loadApplyFormData();
      const flow = stored.flow && typeof stored.flow === "object" ? stored.flow : {};
      const existingPreliminary = stored.preliminary && typeof stored.preliminary === "object" ? stored.preliminary : {};
      saveApplyFormData({
        preliminary: {
          ...existingPreliminary,
          province: customerPayload.province || existingPreliminary.province || "",
        },
      });
      saveApplyFormData({
        flow: {
          ...flow,
          incomeBackPath: "/apply/personal-info",
        },
      });
      await saveCustomerProfileStep("customer", customerPayload);
      navigate("/apply/income-info");
    } catch (error) {
      setSubmitError(error.message || "Unable to save your profile right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
      <VStack maxW="760px" mx="auto" spacing={8} align="stretch">
        <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm" px={{ base: 5, md: 8 }} py={{ base: 7, md: 9 }}>
          <VStack spacing={5} align="stretch">
            <VStack spacing={2} align="stretch" textAlign="center">
              <Heading size="lg">Customer information</Heading>
              <Text color="gray.600" fontSize="sm">
                Entering wrong legal name will slow down your application. Double check for instant service!
              </Text>
            </VStack>

            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired isInvalid={Boolean(errors.firstName)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("firstName") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("firstName") ? "xs" : "sm"}
                      color={isFloating("firstName") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      First name 
                    </FormLabel>
                    <Input  
                      value={values.firstName}
                      onChange={handleChange("firstName")}
                      onFocus={handleFocus("firstName")}
                      onBlur={handleBlur("firstName")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <FormErrorMessage>{errors.firstName}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.lastName)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("lastName") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("lastName") ? "xs" : "sm"}
                      color={isFloating("lastName") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Last name
                    </FormLabel>
                    <Input
                      value={values.lastName}
                      onChange={handleChange("lastName")}
                      onFocus={handleFocus("lastName")}
                      onBlur={handleBlur("lastName")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <FormErrorMessage>{errors.lastName}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.dateOfBirth)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("dateOfBirth") ? "-9px" : "16px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("dateOfBirth") ? "xs" : "sm"}
                      color={isFloating("dateOfBirth") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Date of birth
                    </FormLabel>
                    <Input
                      type="date"
                      value={values.dateOfBirth}
                      onChange={handleChange("dateOfBirth")}
                      onFocus={handleFocus("dateOfBirth")}
                      onBlur={handleBlur("dateOfBirth")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      required
                    />
                    <FormErrorMessage>{errors.dateOfBirth}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.phoneNumber)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("phoneNumber") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("phoneNumber") ? "xs" : "sm"}
                      color={isFloating("phoneNumber") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Phone number
                    </FormLabel>
                    <Input
                      type="tel"
                      value={values.phoneNumber}
                      onChange={handleChange("phoneNumber")}
                      onFocus={handleFocus("phoneNumber")}
                      onBlur={handleBlur("phoneNumber")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <FormErrorMessage>{errors.phoneNumber}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.streetAddress)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("streetAddress") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("streetAddress") ? "xs" : "sm"}
                      color={isFloating("streetAddress") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Street address
                    </FormLabel>
                    <Input
                      value={values.streetAddress}
                      onChange={handleChange("streetAddress")}
                      onFocus={handleFocus("streetAddress")}
                      onBlur={handleBlur("streetAddress")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <FormErrorMessage>{errors.streetAddress}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.city)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("city") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("city") ? "xs" : "sm"}
                      color={isFloating("city") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      City
                    </FormLabel>
                    <Input
                      value={values.city}
                      onChange={handleChange("city")}
                      onFocus={handleFocus("city")}
                      onBlur={handleBlur("city")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <FormErrorMessage>{errors.city}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.province)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("province") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("province") ? "xs" : "sm"}
                      color={isFloating("province") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Province
                    </FormLabel>
                    <Select
                      value={values.province}
                      onChange={handleChange("province")}
                      onFocus={handleFocus("province")}
                      onBlur={handleBlur("province")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      required
                    >
                      <option value="" disabled hidden />
                      <option value="Ontario">Ontario</option>
                      <option value="Alberta">Alberta</option>
                      <option value="British Columbia">British Columbia</option>
                      <option value="Manitoba">Manitoba</option>
                      <option value="Saskatchewan">Saskatchewan</option>
                      <option value="Nova Scotia">Nova Scotia</option>
                      <option value="New Brunswick">New Brunswick</option>
                      <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                    </Select>
                    <FormErrorMessage>{errors.province}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.postalCode)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("postalCode") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("postalCode") ? "xs" : "sm"}
                      color={isFloating("postalCode") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Postal code
                    </FormLabel>
                    <Input
                      value={values.postalCode}
                      onChange={handleChange("postalCode")}
                      onFocus={handleFocus("postalCode")}
                      onBlur={handleBlur("postalCode")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <FormErrorMessage>{errors.postalCode}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.housingType)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("housingType") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("housingType") ? "xs" : "sm"}
                      color={isFloating("housingType") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Rent or mortgage
                    </FormLabel>
                    <Select
                      value={values.housingType}
                      onChange={handleChange("housingType")}
                      onFocus={handleFocus("housingType")}
                      onBlur={handleBlur("housingType")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      required
                    >
                      <option value="" disabled hidden />
                      <option value="Rent">Rent</option>
                      <option value="Mortgage">Mortgage</option>
                    </Select>
                    <FormErrorMessage>{errors.housingType}</FormErrorMessage>
                  </FormControl>

                  {isRentSelected && (
                    <FormControl isRequired isInvalid={Boolean(errors.monthlyRent)} position="relative">
                      <FormLabel
                        position="absolute"
                        left="12px"
                        top={isFloating("monthlyRent") ? "-9px" : "13px"}
                        zIndex={2}
                        px={2}
                        bg="white"
                        fontSize={isFloating("monthlyRent") ? "xs" : "sm"}
                        color={isFloating("monthlyRent") ? "gray.700" : "gray.500"}
                        pointerEvents="none"
                        transition="all 0.2s ease"
                        m={0}
                      >
                        How much is your monthly rent?
                      </FormLabel>
                      <Input
                        type="number"
                        min="0"
                        value={values.monthlyRent}
                        onChange={handleChange("monthlyRent")}
                        onFocus={handleFocus("monthlyRent")}
                        onBlur={handleBlur("monthlyRent")}
                        bg="white"
                        h="52px"
                        borderRadius="lg"
                        borderColor="gray.300"
                        _focus={{ borderColor: "black", boxShadow: "none" }}
                        _hover={{ borderColor: "gray.400" }}
                        placeholder=""
                        required
                      />
                      <FormErrorMessage>{errors.monthlyRent}</FormErrorMessage>
                    </FormControl>
                  )}

                  {isMortgageSelected && (
                    <FormControl isRequired isInvalid={Boolean(errors.monthlyMortgage)} position="relative">
                      <FormLabel
                        position="absolute"
                        left="12px"
                        top={isFloating("monthlyMortgage") ? "-9px" : "13px"}
                        zIndex={2}
                        px={2}
                        bg="white"
                        fontSize={isFloating("monthlyMortgage") ? "xs" : "sm"}
                        color={isFloating("monthlyMortgage") ? "gray.700" : "gray.500"}
                        pointerEvents="none"
                        transition="all 0.2s ease"
                        m={0}
                      >
                        How much is your monthly mortgage?
                      </FormLabel>
                      <Input
                        type="number"
                        min="0"
                        value={values.monthlyMortgage}
                        onChange={handleChange("monthlyMortgage")}
                        onFocus={handleFocus("monthlyMortgage")}
                        onBlur={handleBlur("monthlyMortgage")}
                        bg="white"
                        h="52px"
                        borderRadius="lg"
                        borderColor="gray.300"
                        _focus={{ borderColor: "black", boxShadow: "none" }}
                        _hover={{ borderColor: "gray.400" }}
                        placeholder=""
                        required
                      />
                      <FormErrorMessage>{errors.monthlyMortgage}</FormErrorMessage>
                    </FormControl>
                  )}
                </SimpleGrid>

                <HStack spacing={3} justify="space-between">
                  <Button type="button" variant="outline" borderColor="gray.300" color="gray.700" borderRadius="full" h="50px" px={8} _hover={{ bg: "gray.50" }} onClick={handleBack}>
                    Back
                  </Button>
                  <Button type="submit" isLoading={isSubmitting} bg="black" color="white" borderRadius="full" h="50px" px={8} _hover={{ bg: "gray.900" }}>
                    Continue
                  </Button>
                </HStack>

                {submitError && (
                  <Text color="red.500" fontSize="sm">
                    {submitError}
                  </Text>
                )}
              </VStack>
            </form>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
